import React from 'react'
import { observer } from 'mobx-react-lite'

import Alert from '@mui/material/Alert'

import Snackbar from '@mui/material/Snackbar'
import { alpha, useTheme } from '@mui/material/styles'

import { CategoryIcon } from '@mxenabled/mxui'

import { getCategoryColor } from '../../core/utils/CategoryUtil'
import {
  useBudgetsStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useTransactionStore,
} from '../../common'
import { type Budget, type Transaction } from '../../core'
import { CurrencyDialog, Drawer, Loader } from '../../common/components'
import { TransactionList } from '../../transactions'
import { type TransactionListProps } from '../../transactions/components/shared/TransactionList'

import { getRemainingAmountSpentPercentage } from '../../core/utils/BudgetUtil'

import { BudgetDetailsTopSection } from './shared/BudgetDetailsTopSection'
import { buildDynamicCopy } from '../../core/utils/Localization'

enum Action {
  DELETE = 'delete',
  EDIT = 'edit',
}

interface BudgetDetailsDrawerV2Props {
  onClose?: () => void
  onEvent?: (event: string, amount: string) => void
  onViewHistory?: () => void
  transactionListProps?: TransactionListProps
}

const EVENT_MESSAGES = {
  CANCEL_DELETE: 'Cancel - Delete',
  END_SAVE: 'End - Save',
  END_DELETE: 'End - Delete',
  START_DELETE: 'Start - Delete',
}

// V2 implementation of Budget Details Drawer.
// This will replace BudgetDetailsDrawer
// after the PFM design is finalized
// and aligned with Insights.

const BudgetDetailsDrawerV2 = ({
  onClose,
  onEvent,
  onViewHistory,
  transactionListProps,
}: BudgetDetailsDrawerV2Props) => {
  const {
    alert,
    dateRange,
    deleteBudget,
    selectedBudget,
    selectedSubBudget,
    setAlert,
    setSelectedBudget,
    updateBudget,
  } = useBudgetsStore()
  const { budgets: budgetsCopy, common: commonCopy } = useGlobalCopyStore()
  const { isCopyLoaded, selectedAccountGuids } = useGlobalUiStore()
  const theme = useTheme()
  const { setFilter } = useTransactionStore()

  const [amount, setAmount] = React.useState('')
  const [formAction, setFormAction] = React.useState<Action | null>(null)
  const [isOpen, setIsOpen] = React.useState(false)

  const inProgress = React.useRef(false)

  React.useEffect(() => {
    if (!selectedBudget || selectedSubBudget) return

    setAmount(`${selectedBudget.amount}`)
    setFilter({
      accounts: selectedAccountGuids,
      dateRange,
      custom: ({ category_guid, top_level_category_guid }: Transaction) => {
        const { category_guid: budgetCategoryGuid } = selectedBudget
        return (
          category_guid === budgetCategoryGuid || top_level_category_guid === budgetCategoryGuid
        )
      },
    })
    setIsOpen(true)
  }, [selectedBudget])

  if (!isCopyLoaded) return <Loader />
  if (!selectedBudget) return null

  const handleClose = () => {
    setAmount('')
    setFormAction(null)
    setIsOpen(false)

    setTimeout(() => {
      setSelectedBudget(undefined)
      onClose?.()
    }, 250)
  }

  const handleFormDialogClose = () => {
    if (formAction === Action.DELETE) onEvent?.(EVENT_MESSAGES.CANCEL_DELETE, amount)

    setAmount(selectedBudget.amount ? `${selectedBudget.amount}` : '')
    setFormAction(null)
  }

  const handlePrimaryAction = async () => {
    if (formAction === Action.EDIT) {
      if (inProgress.current) return
      inProgress.current = true

      await updateBudget({
        ...selectedBudget,
        amount: Number(amount),
      } as Budget)
      onEvent?.(EVENT_MESSAGES.END_SAVE, amount)

      setAlert({
        msg: buildDynamicCopy(
          budgetsCopy.alert_budget_updated,
          selectedBudget.category.name,
        ) as string,
        severity: 'success',
      })
      setFormAction(null)

      inProgress.current = false
    }

    if (formAction === Action.DELETE) {
      await deleteBudget(selectedBudget.guid)
      onEvent?.(EVENT_MESSAGES.END_DELETE, amount)

      handleClose()
    }
  }

  const { remainingAmount, spentPercentage, iconColor } = getRemainingAmountSpentPercentage(
    selectedBudget.transaction_total ?? 0,
    selectedBudget.amount ?? 0,
  )

  const buildCopyWithBold = (copyKey: string, content: React.ReactNode): string => {
    return buildDynamicCopy(copyKey, <b>{content}</b>) as string
  }

  return (
    <React.Fragment>
      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        footerStyles={{
          px: 24,
          py: 16,
        }}
        isOpen={isOpen}
        onClose={handleClose}
        onPrimaryAction={() => setFormAction(Action.EDIT)}
        onSecondaryAction={() => {
          onEvent?.(EVENT_MESSAGES.START_DELETE, amount)
          setFormAction(Action.DELETE)
        }}
        primaryText={budgetsCopy.details_edit_button}
        secondaryColor="error"
        secondaryText={budgetsCopy.details_delete_button}
        sx={{
          '& .list-wrapper': {
            pr: 24,
          },
        }}
        title={
          buildDynamicCopy(budgetsCopy.details_type_title, selectedBudget.category.name) as string
        }
      >
        <BudgetDetailsTopSection
          amount={selectedBudget.amount}
          icon={
            <CategoryIcon
              categoryGuid={selectedBudget.category.guid}
              size={44}
              sx={{
                bgcolor: alpha(getCategoryColor(selectedBudget.category.guid, theme), 0.15),
                border: `1px solid ${getCategoryColor(selectedBudget.category.guid, theme)}`,
                color: 'text.primary',
              }}
            />
          }
          iconColor={iconColor}
          onViewHistory={onViewHistory}
          remainingAmount={remainingAmount}
          spentPercentage={spentPercentage}
          startDate={(dateRange.start.getTime() ?? 0) / 1000}
          totalTransaction={selectedBudget.transaction_total}
        />
        <TransactionList {...transactionListProps} />
        {alert && (
          <Snackbar
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            autoHideDuration={3500}
            onClose={() => setAlert(null)}
            open={Boolean(alert)}
          >
            <Alert
              closeText=""
              onClose={() => setAlert(null)}
              severity={alert?.severity ?? 'success'}
              variant="filled"
            >
              {alert?.msg}
            </Alert>
          </Snackbar>
        )}
      </Drawer>
      {formAction && (
        <CurrencyDialog
          closeAriaLabel={commonCopy.close_aria}
          content={buildCopyWithBold(
            budgetsCopy[formAction === Action.EDIT ? 'edit_budget_content' : 'delete_description'],
            selectedBudget.category.name,
          )}
          handleInputChange={(value) => setAmount(value.toString())}
          handleModalClose={handleFormDialogClose}
          handlePrimaryAction={handlePrimaryAction}
          handleSecondaryAction={handleFormDialogClose}
          inputLabel={formAction === Action.EDIT ? budgetsCopy.details_edit_title : ''}
          inputValue={amount}
          isOpen={true}
          primaryColor={formAction === Action.EDIT ? 'primary' : 'error'}
          primaryText={
            budgetsCopy[formAction === Action.EDIT ? 'add_save_button' : 'details_delete_button']
          }
          secondaryColor={formAction === Action.EDIT ? 'primary' : 'inherit'}
          secondaryText={commonCopy.cancel_button}
          title={budgetsCopy[formAction === Action.EDIT ? 'details_edit_title' : 'delete_title']}
        />
      )}
    </React.Fragment>
  )
}

export default observer(BudgetDetailsDrawerV2)
